import { ColorByOption } from './ClinicMapView';

// Filter option types
export interface FilterOption {
  value: string;
  label: string;
  color: string;
}

export const PLAN_OPTIONS: FilterOption[] = [
  { value: 'ehr', label: 'EHR', color: '#10B981' },
  { value: 'premium', label: 'Premium', color: '#8B5CF6' },
  { value: 'plus', label: 'Plus', color: '#3B82F6' },
  { value: 'basic', label: 'Basic', color: '#6B7280' },
];

export const STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active', color: '#10B981' },
  { value: 'churned', label: 'Churned', color: '#EF4444' },
];

export const POSTING_SCHEDULE_OPTIONS: FilterOption[] = [
  { value: 'A', label: 'Schedule A', color: '#F59E0B' },
  { value: 'B', label: 'Schedule B', color: '#06B6D4' },
  { value: 'not_set', label: 'Not Set', color: '#9CA3AF' },
];

export const POSTING_PLATFORM_OPTIONS: FilterOption[] = [
  { value: 'Facebook', label: 'Facebook', color: '#1877F2' },
  { value: 'Instagram', label: 'Instagram', color: '#E4405F' },
  { value: 'Linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { value: 'not_set', label: 'Not Set', color: '#9CA3AF' },
];

export const POST_TYPE_OPTIONS: FilterOption[] = [
  { value: 'Self-post', label: 'Self-post', color: '#8B5CF6' },
  { value: 'Automated', label: 'Automated', color: '#10B981' },
  { value: 'not_set', label: 'Not Set', color: '#9CA3AF' },
];

export const DEFAULT_STATUS = ['active'];

export const COLOR_BY_OPTIONS: { value: ColorByOption; label: string }[] = [
  { value: 'plan', label: 'Plan' },
  { value: 'status', label: 'Status' },
  { value: 'schedule', label: 'Posting Schedule' },
  { value: 'postType', label: 'Post Type' },
  { value: 'platform', label: 'Platform' },
];

// Labels for legend items
export const LEGEND_LABELS: Record<ColorByOption, Record<string, string>> = {
  plan: {
    ehr: 'EHR',
    premium: 'Premium',
    plus: 'Plus',
    basic: 'Basic',
    unknown: 'Unknown',
  },
  status: {
    active: 'Active',
    churned: 'Churned',
    unknown: 'Unknown',
  },
  schedule: {
    A: 'Schedule A',
    B: 'Schedule B',
    unknown: 'Not Set',
  },
  postType: {
    'Self-post': 'Self-post',
    Automated: 'Automated',
    unknown: 'Not Set',
  },
  platform: {
    Facebook: 'Facebook',
    Instagram: 'Instagram',
    Linkedin: 'LinkedIn',
    multiple: 'Multiple',
    unknown: 'Not Set',
  },
};

// US State abbreviation to full name mapping
export const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  // Canadian provinces
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

// Reverse mapping: full name to abbreviation
const STATE_NAME_TO_ABBREV: Record<string, string> = Object.entries(US_STATE_NAMES).reduce(
  (acc, [abbrev, name]) => {
    acc[name.toLowerCase()] = abbrev;
    return acc;
  },
  {} as Record<string, string>
);

// Helper function to get full state name
export const getStateName = (abbrev: string): string => {
  return US_STATE_NAMES[abbrev] || abbrev;
};

// Normalize state value to standard abbreviation
// Handles: "AL", "al", "Alabama", "alabama", etc.
export const normalizeStateToAbbrev = (state: string | undefined | null): string => {
  if (!state) return '';
  const trimmed = state.trim();
  if (!trimmed) return '';

  // Check if it's already an abbreviation (1-2 uppercase letters after normalization)
  const upper = trimmed.toUpperCase();
  if (US_STATE_NAMES[upper]) {
    return upper;
  }

  // Check if it's a full state name
  const lower = trimmed.toLowerCase();
  if (STATE_NAME_TO_ABBREV[lower]) {
    return STATE_NAME_TO_ABBREV[lower];
  }

  // Return original if not found (could be international or unknown)
  return trimmed;
};

// Type for custom color overrides
export type CustomColors = Record<ColorByOption, Record<string, string>>;

// State info type for state selectors
export interface StateInfo {
  abbrev: string;
  fullName: string;
  count: number;
}
